import { useState, ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { userApiService } from '@/shared/services/userApiService';
import { Input } from '@/shared/components/ui';
import { ROUTES } from '@/shared/config';
import { useSafeThemeClasses } from '@/shared/theme';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';

function ForgotPasswordPage() {
	const theme = useSafeThemeClasses();
	const [email, setEmail] = useState('');
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
		if (error) setError('');
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		if (!email.trim()) {
			setError('Email is required');
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await userApiService.forgotPassword(email.trim());
			if (response.success) {
				setSubmitted(true);
			} else {
				toast.error(response.error?.message || 'Something went wrong. Please try again.');
			}
		} catch (err) {
			logger.error('Forgot password request failed', 'ForgotPasswordPage', { error: err });
			toast.error('Something went wrong. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={`min-h-screen flex items-center justify-center px-4 py-12 transition-opacity duration-500 ${theme.background}`}>
			<div className="w-full max-w-md">
				<div className={`${theme.card} rounded-2xl p-8 space-y-6`}>
					<div className="text-center">
						<h1 className={`text-2xl font-bold ${theme.textHeading} tracking-tight`}>Reset your password</h1>
						<p className={`mt-2 text-sm ${theme.textSecondary}`}>
							Enter the email on your account and we'll send you a link to reset your password.
						</p>
					</div>

					{submitted ? (
						<div className="space-y-6">
							<div className={`${theme.alertSuccess} rounded-xl p-4 text-sm`}>
								If that email is registered, a password reset link is on its way. It expires in 1 hour.
							</div>
							<Link
								to={ROUTES.LOGIN}
								className={`block w-full py-3 px-6 rounded-xl font-semibold text-center transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
							>
								Back to Sign In
							</Link>
						</div>
					) : (
						<form onSubmit={handleSubmit} className="space-y-5">
							<div className="form-control">
								<Input
									type="email"
									id="email"
									name="email"
									placeholder="you@example.com"
									value={email}
									onChange={handleChange}
									error={error}
									required
									className={`w-full px-4 py-3 rounded-xl ${theme.input} ${theme.textPrimary} ${theme.placeholderText} transition-all duration-300 focus:scale-[1.01] focus:ring-2 focus:ring-blue-500/50`}
								/>
							</div>

							<button
								type="submit"
								disabled={isSubmitting}
								className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center ${
									isSubmitting ? `${theme.buttonDisabled} ${theme.buttonTextDisabled}` : `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
								}`}
							>
								{isSubmitting ? 'Sending...' : 'Send reset link'}
							</button>

							<Link
								to={ROUTES.LOGIN}
								className={`block text-center text-sm font-medium hover:underline ${theme.textAccent}`}
							>
								Back to Sign In
							</Link>
						</form>
					)}
				</div>
			</div>
		</div>
	);
}

export default ForgotPasswordPage;
